import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { styled } from 'styled-components';
import Select from 'react-select';
import { returnThunks } from 'store/reducers/returnSlice';
import { configSelectors } from 'store/reducers/configSlice';
import useAppSelector from 'hooks/useAppSelector';
import useAppDispatch from 'hooks/useAppDispatch';
import { HandleReturnItemChange } from 'components/Orders/Order';
import { useState } from 'react';
import { ReturnItem, ReturnOrderLine } from 'returnCenter-api';
import { CellInputRefs } from 'hooks/useKeyboardNavigation';

type ReasonCellProps = {
    item: ReturnItem;
    itemId?: number | string;
    handleReturnItemChange?: HandleReturnItemChange;
    disabled?: boolean;
    orderline?: ReturnOrderLine;
    rowIndex?: string | number;
    colIndex?: string | number;
    cellInputRefs?: CellInputRefs;
    handleArrowKeyDown?: (e: any, orderId: any, rowIndex: any, colIndex: any) => void;
    cellRefPrefix?: string;
    hasDecisionCodes?: boolean;
};

export const REASON_ITEM_REF_PREFIX = 'reason-item-';

const ReasonCell = ({
    item,
    itemId,
    handleReturnItemChange,
    orderline,
    rowIndex,
    colIndex,
    cellInputRefs,
    handleArrowKeyDown,
    cellRefPrefix = '',
    disabled = false,
    hasDecisionCodes = false,
}: ReasonCellProps) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const dispatch = useAppDispatch();

    const reasonCodes = useAppSelector(configSelectors.selectReasonCodesMap);

    if (disabled) {
        const selectedReason = reasonCodes.find((rc) => rc.value === item.reasonCode);
        return (
            <Wrapper column gap="0.5rem" $grow={1} className="reason-wrapper">
                <Text fontSize={12} as="span">
                    {selectedReason?.label ?? '-'}
                </Text>
            </Wrapper>
        );
    }

    return (
        <Wrapper column gap="0.5rem" $grow={1} className="reason-wrapper">
            <Flex gap="0.5rem" className="reason-item">
                <StyledSelect
                    isDisabled={disabled}
                    menuIsOpen={menuOpen}
                    onMenuOpen={() => setMenuOpen(true)}
                    onMenuClose={() => setMenuOpen(false)}
                    data-row={rowIndex}
                    data-row-cell={colIndex}
                    data-item-id={itemId}
                    options={reasonCodes}
                    key={itemId}
                    value={reasonCodes.find((rc) => rc.value === item.reasonCode) || null}
                    ref={(el) => {
                        const key = `${cellRefPrefix}${rowIndex}-${colIndex}`;
                        const reasonItem = `${REASON_ITEM_REF_PREFIX}${itemId}`;
                        if (cellInputRefs) cellInputRefs.current[key] = el;
                        if (cellInputRefs) cellInputRefs.current[reasonItem] = el;
                    }}
                    onChange={(selectedOption) => {
                        const { value } = selectedOption as {
                            value: number;
                            label: string;
                        };

                        handleReturnItemChange?.(
                            orderline?.orderNumber ?? '',
                            orderline?.sku ?? '',
                            orderline?.orderLineNumber ?? '',
                            itemId ?? -1,
                            'reasonCode',
                            value,
                        );

                        // Evaluate decision code if decision codes are enabled and not manually overridden
                        if (hasDecisionCodes && !(item as any).decisionCodeIsManual) {
                            dispatch(
                                returnThunks.evaluateDecisionCode({
                                    orderNumber: orderline?.orderNumber ?? '',
                                    sku: orderline?.sku ?? '',
                                    orderLineNumber: orderline?.orderLineNumber ?? '',
                                    itemId: itemId ?? -1,
                                    reasonCode: value,
                                    dispatchDate: orderline?.dispatchDate || null,
                                    orderType: orderline?.orderType,
                                }),
                            );
                        }

                        setMenuOpen(false);
                    }}
                    menuPortalTarget={document.body}
                    tabIndex={-1}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !menuOpen) {
                            e.preventDefault();
                            e.stopPropagation();
                            setMenuOpen(true);
                            return;
                        }
                        if (e.key === 'Escape' && menuOpen) {
                            e.preventDefault();
                            e.stopPropagation();
                            setMenuOpen(false);
                            return;
                        }
                        // Only handle arrow navigation when menu is closed
                        if (!menuOpen) {
                            handleArrowKeyDown?.(e, itemId, rowIndex, colIndex);
                        }
                    }}
                />
            </Flex>
        </Wrapper>
    );
};

export default ReasonCell;

export const StyledSelect = styled(Select)`
    display: flex;
    min-width: 0;
    flex: 1;
    height: 37px;
    white-space: nowrap;

    /* .react-select__menu {
        z-index: 9999;
        width: auto !important;
        min-width: 100% !important;
    }

    .react-select__control {
        width: auto;
        min-width: 800px !important;
        max-width: 100% !important;
    } */

    > * {
        width: auto !important;
        min-width: 100% !important;
    }
`;

const Wrapper = styled(Flex)`
    width: 135px;
`;
